import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { FusionAuthProvider } from "@fusionauth/react-sdk";
import Navbar from "./pages/navbar";
import SecondaryNavbar from "./pages/secondaryNavbar";
import LandingPage from "./pages/LandingPage";
import Company from "./pages/Company/Company";
import HowWeWork from "./pages/HowWeWork";
import Partners from "./pages/Partners";
import Investing from "./pages/Investing";
import Support from "./pages/Support/Support";
import Services from "./pages/Services";
import LienAssist from "./pages/lienAssist";
import TMS from "./pages/TMS";
import Zeus from "./pages/Zeus";
import Sheriff from "./pages/Sheriff";
import Links from "./pages/Links";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties(New)/Properties";
import Unsubscribe from "./pages/Unsubscribe/Unsubscribe";
import Profile from "./pages/Profile";
import MondayBoards from "./pages/MondayBoards";
import DocumentsTable from "./pages/DocumentsTable";
import InactivityTimer from "./InactivityTimer"; // Import the InactivityTimer component
import { Provider, useDispatch } from "react-redux";
import store from "./redux/store"; // Ensure the path is correct
import styles from "./css/Footer.module.css";
import { setupStorageListener } from "./redux/userSlice";
import Hotjar from "@hotjar/browser";
import County from "./pages/County/County";
import KnowledgeBase from "./pages/KnowledgeBase/KnowledgeBase";
import { getOAuthRedirectUri } from "./config";
import AuthGate from "./components/AuthGate";
import LoadingSpinner from "./pages/LoadingSpinner";
import RoleBasedRedirect from "./components/roleBasedRedirect";

// HOTJAR INIT STUFF
const siteId = 2564574;
const hotjarVersion = 6;

// TURNING ON DEBUGING
//Hotjar.init(siteId, hotjarVersion, { debug: true });
Hotjar.init(siteId, hotjarVersion);

// Promise-based sleep helper. Defined at module scope (not inside a loop) so
// each call captures `ms` as a fresh parameter rather than closing over a
// mutable loop variable — avoids the no-loop-func lint warning that occurs
// when the same Promise pattern is inlined in retryLoadEnvVariable's while loop.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper component to set up cross-tab synchronization.
const StorageListener = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    // Set up the storage listener and get the cleanup function.
    const cleanup = setupStorageListener(dispatch);
    // Return the cleanup function on unmount.
    return cleanup;
  }, [dispatch]);
  return null;
};

function App() {
  const [isEnvReady, setIsEnvReady] = useState(false);

  const retryLoadEnvVariable = async (envVariableKey, maxRetries = 5, initialDelay = 700) => {
    let retries = 0;
    let delay = initialDelay;
    while (retries < maxRetries) {
      const value = process.env[envVariableKey];
      if (value) {
        return value;
      } else {
        retries++;
        const currentDelay = delay;
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        delay *= 2; // Exponential backoff
      }
    }
    throw new Error(`Environment variable ${envVariableKey} is undefined after ${maxRetries} retries.`);
  };

  useEffect(() => {
    const loadEnvVariables = async () => {
      try {
        // Wait for all critical environment variables to be loaded.
        await retryLoadEnvVariable("REACT_APP_CLIENT_ID");
        await retryLoadEnvVariable("REACT_APP_SERVER_URL");
        await retryLoadEnvVariable("REACT_APP_GOOGLE_MAPS_API");
        await retryLoadEnvVariable("REACT_APP_SERVER_API_KEY");
        await retryLoadEnvVariable("REACT_APP_MONDAY_API_KEY");
        await retryLoadEnvVariable("REACT_APP_BLOB_CONNECTION_STRING");
        await retryLoadEnvVariable("REACT_APP_TSM_URL");
        setIsEnvReady(true);
      } catch (error) {
        // Handle error silently.
      }
    };

    loadEnvVariables();
  }, []);

  // Third-party scripts (Hotjar, reCAPTCHA, etc.) inject hidden fields without
  // labels — WAVE flags them as errors on every route. Use title because WAVE
  // counts it as a valid label (same pattern as secondaryNavbar Google fixes).
  useEffect(() => {
    if (!isEnvReady) return;

    const labelHiddenThirdPartyFields = () => {
      let foundUnlabeled = false;
      const selectors = [
        'textarea.g-recaptcha-response',
        'textarea[id^="g-recaptcha-response"]',
        'textarea[name="g-recaptcha-response"]',
        'textarea[data-hj-suppress]',
        'input[data-hj-suppress]',
      ];
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (!el.getAttribute('title') && !el.getAttribute('aria-label')) {
            el.setAttribute('title', 'Auxiliary hidden field');
            foundUnlabeled = true;
          }
        });
      });
      return !foundUnlabeled;
    };

    if (labelHiddenThirdPartyFields()) return;

    const observer = new MutationObserver(() => {
      if (labelHiddenThirdPartyFields()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isEnvReady]);

  if (!isEnvReady) {
    return <div role="status">Loading environment variables...</div>;
  }

  const config = {
    clientId: process.env.REACT_APP_CLIENT_ID,
    serverUrl: process.env.REACT_APP_SERVER_URL,
    redirectUri: getOAuthRedirectUri(),
    shouldAutoFetchUserInfo: true,
    shouldAutoRefresh: true,
  };

  return (
    <Provider store={store}>
      {/* Opt-in to React Router v7 behavior on v6 to silence future-flag warnings and catch v7 breakage incrementally instead of at the major upgrade. */}
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <FusionAuthProvider {...config}>
          {/* Set up cross-tab logout synchronization */}
          <StorageListener />
          {/* This component handles role-based redirection and will redirect admin/SRI users to the dashboard */}
          <RoleBasedRedirect />
          {/* Wrap the main application with InactivityTimer */}
          <InactivityTimer>
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <SecondaryNavbar />
            {/* <div style={{backgroundColor: "#BC0000", minHeight: "30px", display: "flex", width: "100%", color: "white", paddingLeft: "10px", paddingTop: "5px", paddingBottom: "5px"}}>We're currently experiencing problems with our services. You may not be able to use them at the moment. Our team is investigating and working to restore access as quickly as possible.</div> */}
            <div
              role="region"
              aria-label="Site announcement"
              className="w-100 d-flex align-items-center justify-content-center"
              style={{ minHeight: "30px", backgroundColor: "#cff4fc", color: "#055160" }}
            >
              <span>Access to Properties and Auction List have been moved to "Sale Information" in the navbar.</span>
            </div>
            <Navbar />
            <div className={styles.appContainer}>
              <main id="main-content" tabIndex="-1" className={styles.mainContent}>
                <AuthGate fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/company" element={<Company />} />
                    <Route path="/work" element={<HowWeWork />} />
                    <Route path="/partners" element={<Partners />} />
                    <Route path="/investing" element={<Investing />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/lienAssist" element={<LienAssist />} />
                    <Route path="/tms" element={<TMS />} />
                    <Route path="/zeus" element={<Zeus />} />
                    <Route path="/sheriff" element={<Sheriff />} />
                    <Route path="/links" element={<Links />} />
                    <Route path="/monday" element={<MondayBoards />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/properties" element={<Properties />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/documents" element={<DocumentsTable />} />
                    <Route path="/county/:stateCode/:countyName" element={<County />} />
                    <Route path="/knowledge-base" element={<KnowledgeBase />} />
                    {/* Add more routes as needed */}
                  </Routes>
                </AuthGate>
              </main>
            </div>
          </InactivityTimer>
        </FusionAuthProvider>
      </Router>
    </Provider>
  );
}

export default App;
